import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from '@getgo/chameleon-web/react';
import { useTranslation } from 'react-i18next';
import momentJS from 'moment-timezone';
import { useParams } from 'react-router';
import API from '../utils/api';
import { useEventContext } from '../context/eventContext';
import { useUserContext } from '../context/userContext';
import EventRegistrationForm from './views/eventRegistrationForm';
import EventHostedByDetails from './views/eventHostedByDetails';
import AppHeader from '../app/appHeader';
import Footer from '../footer/footer';
import EventImage from './views/eventImage';
import EventOverview from './views/eventOverview';
import Agenda from '../agenda/agenda';
import EventSpeakers from './views/eventSpeakers';
import { eventDataViewModel } from '../utils/eventDataFormat';
import EventRegisteredUser from './views/eventRegisteredUser';
import { DateUtils } from '../../utils/dateUtils';
import { EventCopyLink } from './views/eventCopyLink';
import EventNotAvailable from '../eventNotAvailable/eventNotAvailable';
import { getCookie } from '../utils/browserCookies';
import { onLandingOfEventPageTracker } from '../../mixpanel/Feature/eventPageLoadTracker';
import { onClickOfEventSpeakerTracker } from '../../mixpanel/Feature/eventTracker';
import setMixPanelProperties from '../../mixpanel/setMixPanelProperties';
import FullScreenLoader from '../utils/fullScreenLoader/FullScreenLoader';
import useTimeTable from '../hooks/useTimeTable';
import './styles/events.scss';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, StripeElementLocale } from '@stripe/stripe-js';
import { useFlags } from 'launchdarkly-react-client-sdk';

// Deprecated: Remove ThemeProvider from chameleon-chakra-ui
// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISH_KEY || '', {
  apiVersion: process.env.REACT_APP_STRIPE_VERSION,
});

const Event: FC = () => {
  const { proxyQuestionareCalls } = useFlags();
  const { t, i18n } = useTranslation();
  const [isLoading, setLoading] = useState(true);
  const { setEventDetails, eventDetails } = useEventContext();
  const { userDetail, setUserDetail } = useUserContext();
  const { eventId, eventRegistrationId } = useParams() as any;
  const [activeSpeakerId, setActiveSpeakerId] = useState('');
  const [isMobOrTabView, setMoborTabView] = useState(
    window.matchMedia('(max-width: 1024px)').matches,
  );
  const [isFormVisible, setFormVisibility] = useState(
    !window.matchMedia('(max-width: 1024px)').matches,
  );
  const divRef = useRef<HTMLDivElement>(null);

  const hideHCaptcha = eventDetails?.hcaptchaDisabled ?? false;

  const handleEventSpeakerChange = (index: number, speakerId: string) => {
    setActiveSpeakerId(speakerId);
    onClickOfEventSpeakerTracker({ speakerId, eventKey: eventId });
    (window as any).scrollBy(0, speakerListRef.current[speakerId].getBoundingClientRect().top - 40);
  };

  const handleUpdateLanguage = (lang: string) => {
    if (lang === i18n.language) return;
    i18n.changeLanguage(lang, () => {
      // TO-DO Change language for date
      document.documentElement.lang = lang === 'zh' ? 'zh-cn' : lang;
    });
  };

  const conferenceSchedule = useMemo(() => {
    if (!eventDetails?.conferences?.length) return [];
    return eventDetails.conferences.map(({ start }) => start);
  }, [eventDetails]);

  const updateCurrentEvent = async () => {
    const { data, status } = await API.getEventDetails(eventId);
    if (status !== 200) return;
    handleUpdateLanguage(data?.locale?.substr(0, 2).toLowerCase());
    setEventDetails(eventDataViewModel(data, true));
  };

  useTimeTable({
    schedule: conferenceSchedule,
    action: updateCurrentEvent,
    isUserRegistered: !!userDetail,
  });

  useEffect(() => {
    const getEvent = async () => {
      try {
        const eventData = await API.getEventDetails(eventId);
        let questionnaireResponse = null;
        let paymentDetailsResponse = null;
        let couponExistsResponse = null;
        try {
          questionnaireResponse = await getQuestionnaire(eventData);
        } catch (error) {
          console.log('error', error);
        }
        try {
          [paymentDetailsResponse, couponExistsResponse] = await Promise.all([
            getPaymentDetails(eventId),
            getCouponExistenceInfo(eventId),
          ]);
        } catch (e) {
          console.log(e);
        }
        const lang = eventData?.data?.locale?.substr(0, 2).toLowerCase();
        handleUpdateLanguage(lang);

        if (
          (eventData?.status === 200 && questionnaireResponse == null) ||
          (eventData?.status === 200 && questionnaireResponse?.status === 200)
        ) {
          const eventViewDetails = eventDataViewModel(eventData.data);
          eventViewDetails['questionnaireDetails'] = questionnaireResponse?.data;
          eventViewDetails['paymentDetailsResponse'] = paymentDetailsResponse?.data;
          eventViewDetails['paymentDetails'] = paymentDetailsResponse?.data;
          eventViewDetails['couponExists'] = couponExistsResponse?.data;
          setEventDetails(eventViewDetails);
          /* Favorite Disabled
          cleanOldFavoriteInLocalStorage(
            eventData?.data?.conferences?.map((eventDetail: any) => eventDetail?.conferenceKey),
          );
           */
        } else {
          setEventDetails(null);
          setLoading(false);
        }
      } catch (error) {
        console.log('error', error);
      }
    };

    const getQuestionnaire = async (eventData: any) => {
      if (eventData?.status == 200) {
        const { questionnaireKey, locale } = eventData.data;
        const localeWithHyphen = locale.replace('_', '-');
        if (questionnaireKey) {
          return await API.getQuestionnaire(
            questionnaireKey,
            localeWithHyphen,
            proxyQuestionareCalls,
          );
        }
      }
      return null;
    };

    const getUser = async () => {
      if (eventRegistrationId) {
        const requestData = {
          eventRegistrationId: eventRegistrationId,
        };
        const userDetail = await API.getUserDetails(requestData);
        if (userDetail.status === 200) {
          const user = userDetail.data.user;
          setUserDetail({ ...user, eventRegistrationId: eventRegistrationId });
          // set mix panel properties like userId, eventId, eventRegistrationId
          setMixPanelProperties(user, eventId, eventRegistrationId);
          onLandingOfEventPageTracker({ eventKey: eventId, description: 'Landing On Event Page' });
        }
      }
    };

    const getCouponExistenceInfo = async (eventId: string) => {
      return await API.checkCouponExists(eventId);
    };

    const getPaymentDetails = async (eventId: string) => {
      return await API.getPaymentDetails(eventId);
    };

    if (eventId && getCookie(eventId)) {
      const userDetails = JSON.parse(getCookie(eventId) || '{}');
      setUserDetail({ ...userDetails });
      // set mix panel properties like userId, eventId, eventRegistrationId
      setMixPanelProperties(userDetails, eventId, userDetails.eventRegistrationId);
      onLandingOfEventPageTracker({ eventKey: eventId, description: 'Landing On Event Page' });
    }
    getEvent();
    getPaymentDetails(eventId);
    getCouponExistenceInfo(eventId);

    getUser();

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (eventDetails?.locale.includes(i18n.language)) {
      setLoading(false);
    }
  }, [eventDetails?.locale, i18n.language]);

  const handleResize = () => {
    setTimeout(() => {
      if (window.matchMedia('(max-width: 1024px)').matches) {
        setMoborTabView(true);
      } else {
        setMoborTabView(false);
        setFormVisibility(true);
      }
      disableEventRootScroll(false);
    }, 500);
  };

  const disableEventRootScroll = (shouldDisableScroll: boolean) => {
    const rootElement = divRef?.current?.closest('#root');
    if (rootElement) {
      shouldDisableScroll
        ? rootElement.classList.add('eventroot')
        : rootElement.classList.remove('eventroot');
    }
  };

  const openForm = (regFormRef: HTMLDivElement | null) => {
    disableEventRootScroll(true);
    setFormVisibility(true);
    if (regFormRef) {
      regFormRef.scrollTop = 0;
    }
  };

  const closeForm = () => {
    if (isFormVisible && isMobOrTabView) {
      disableEventRootScroll(false);
      setFormVisibility(false);
    }
  };

  const speakerListRef = useRef({} as { [key: string]: any });

  if (isLoading) return <FullScreenLoader />;
  if (!eventDetails) return <EventNotAvailable />;

  return (
    <Elements
      stripe={stripePromise}
      options={{ locale: (eventDetails?.locale?.split('_')[0] as StripeElementLocale) || 'en' }}
    >
      <div
        ref={divRef}
        className={
          !userDetail && isFormVisible ? 'event-container event-details-holder' : 'event-container'
        }
      >
        <AppHeader />
        <div className="event-grid">
          <div className="event-grid-item-1">
            <EventImage brandingImage={eventDetails.image} />
            <div>
              <EventCopyLink title={eventDetails.title} eventKey={eventDetails?.eventKey} />
              <Typography color="text-02">
                {`${DateUtils.formatLocalizedTimeAndMonthRange(
                  eventDetails.start,
                  eventDetails.end,
                  i18n.language,
                )} (${momentJS.tz(Intl.DateTimeFormat().resolvedOptions().timeZone).zoneAbbr()})`}
              </Typography>
            </div>
            <div>
              <div>
                {eventDetails?.description && (
                  <section id="overview" className="event-section-content">
                    <h6 className="event-section-header">{t('event.overview')}</h6>
                    <EventOverview eventDetail={eventDetails} />
                  </section>
                )}
                {eventDetails?.conferences?.length !== 0 && (
                  <section id="agenda" className="event-section-content">
                    <h6 className="event-section-header">{t('event.agenda')}</h6>
                    <Agenda
                      eventDetail={eventDetails}
                      handleEventSpeakerChange={handleEventSpeakerChange}
                      userDetail={userDetail}
                      isMobOrTabView={isMobOrTabView}
                    />
                  </section>
                )}
                {eventDetails?.speakers?.length !== 0 && (
                  <section id="speakers" className="event-section-content">
                    <h6 className="event-section-header">{t('event.speakers')}</h6>
                    <EventSpeakers
                      eventDetail={eventDetails}
                      activeSpeakerId={activeSpeakerId}
                      speakerListRef={speakerListRef}
                      eventKey={eventId}
                    />
                  </section>
                )}
              </div>
            </div>
          </div>
          <div className="event-grid-item-2">
            {userDetail ? (
              <EventRegisteredUser userDetail={userDetail} eventDetail={eventDetails} />
            ) : (
              <EventRegistrationForm
                hideHCaptcha={hideHCaptcha}
                closeForm={closeForm}
                isFormVisible={isFormVisible}
                openForm={openForm}
              />
            )}
            {(!isMobOrTabView || userDetail) && eventDetails?.organizers?.length && (
              <EventHostedByDetails eventDetail={eventDetails} />
            )}
          </div>
        </div>
        <Footer />
      </div>
    </Elements>
  );
};

export default Event;
